import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import { PersonHelpers } from '@frontend/api-person';
import { formatDate } from '@frontend/date';
import { i18next } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { ExtendedAppointment } from '../../types';
import { getDurationFromHours } from '../calendar-view/utils';
import { AppointmentStatusType } from './appointment-event-card/types';

export const APPOINTMENT_STATUS_CACHE_TIME_IN_MINUTES = 2;

export interface ScheduleEventPersonInfo {
  id: string;
  name: string;
  status: string;
  age: string;
  gender: string;
  householdId: string;
  sourceId?: string;
  PMSId?: string;
}

export interface ScheduleEventAppointmentDetails {
  date: string;
  duration: string;
  type?: string;
  status?: string;
  externalStatus?: string;
  practitioner: string;
  providerIds: string[];
  operatory?: string;
  referenceId?: string;
  // for v3 calendar events
  isOverlapping?: boolean;
  appointmentTypeId?: string;
  displayOptions?: {
    primaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
  };
}

interface ScheduleEventAppointmentInfo {
  id: string;
  locationId: string;
  personInfo: ScheduleEventPersonInfo;
  appointmentDetails: ScheduleEventAppointmentDetails;
  petInfo: { serviceableIds?: string[] };
}

const getEventDurationFromStartDate = (startDate: string, duration?: number) => {
  const startHour = formatDate(dayjs(startDate).toDate(), 'hh:mm A');
  const endHour = duration ? formatDate(dayjs(startDate).add(duration, 'm').toDate(), 'hh:mm A') : '';
  return getDurationFromHours(startHour, endHour);
};

export const prepareAppointmentInfo = (
  appointments: (ExtendedAppointment & Pick<CalendarEvent, 'referenceId'>)[],
  appointmentId: string,
  getLocationTimezoneAbbr?: (locationId: string) => string
): ScheduleEventAppointmentInfo | undefined => {
  const appointment =
    appointments.length === 1 ? appointments[0] : appointments.find((appointment) => appointment.id === appointmentId);

  if (!appointment) {
    return undefined;
  }

  // Use the first name and last name from the appointment object to create the full name of the person associated with the appointment
  const personName = getFullName(appointment.person ?? {}) || i18next.t('Unknown', { ns: 'scheduleCalendarEvents' });

  const personAge = PersonHelpers.getAge({ Birthdate: appointment.person?.birthDate });
  const personAgeText = personAge
    ? i18next.t('{{age}} yrs', {
        ns: 'scheduleCalendarEvents',
        age: personAge,
      })
    : '';

  const duration = !!appointment.duration
    ? getEventDurationFromStartDate(appointment.start!, appointment.duration)
    : formatDate(dayjs(appointment.start!), 'hh:mm A');
  const appointmentInfo: ScheduleEventAppointmentInfo = {
    id: appointmentId,
    locationId: appointment.locationId ?? '',
    personInfo: {
      id: appointment.person?.personId || appointment.customerIds?.[0] || '',
      name: personName,
      status: appointment.person?.status ?? '',
      age: personAgeText,
      gender: appointment.person?.gender ?? '',
      householdId: appointment.person?.weaveHouseholdId ?? '',
      sourceId: appointment.person?.sourceId,
      PMSId: appointment.person?.pmId,
    },
    petInfo: { serviceableIds: appointment.serviceableIds },
    appointmentDetails: {
      date: formatDate(dayjs(appointment.start).toDate(), 'MMM Do, YYYY'),
      duration: duration ? `${duration} ${getLocationTimezoneAbbr?.(appointment.locationId ?? '')}`.trim() : '',
      type: appointment.type,
      status: appointment.statusOfficeView,
      externalStatus: appointment.externalStatus ?? '',
      practitioner: appointment.practitionerName ?? '',
      providerIds: appointment.providerIds || [],
      operatory: appointment.operatory ?? '',
      referenceId: appointment.referenceId ? appointment.referenceId : appointment.id,
      isOverlapping: appointment.isOverlapping ?? false,
      appointmentTypeId: appointment.appointmentTypeId || '',
      displayOptions: {
        primaryColor: appointment.displayOptions?.primaryColor || undefined,
        backgroundColor: appointment.displayOptions?.backgroundColor || undefined,
        textColor: appointment.displayOptions?.textColor || undefined,
      },
    },
  };

  return appointmentInfo;
};

export const getLatestUpdatedAppointmentStatus = (appointmentStatus: AppointmentStatusType) => {
  return appointmentStatus.updatedAt
    ? dayjs().diff(dayjs(appointmentStatus.updatedAt), 'minutes') > APPOINTMENT_STATUS_CACHE_TIME_IN_MINUTES
    : false;
};

export const formatStringForComparison = (str: string) => {
  if (!str) return '';
  return str.toLowerCase().trim();
};

const getIsAppointmentTimeLessThanOrEqualTo30Minutes = (duration: string): boolean => {
  // duration parameter example: "12:30 AM - 1:15 AM"
  const [startStr, endStr] = duration.split(' - ').map((s) => s.trim());

  const start = dayjs(startStr, 'hh:mm A');
  const end = dayjs(endStr, 'hh:mm A');

  const diff = end.isBefore(start) ? end.add(1, 'day').diff(start, 'minute') : end.diff(start, 'minute');

  return diff <= 30;
};

// Returns true if the duration is 30 minutes or less, otherwise false
export const shouldRenderStatusIconInline = (
  duration: string,
  hasAppointmentStatusIcon: boolean,
  hasInsuranceStatusIcon: boolean
): boolean => {
  if (!hasAppointmentStatusIcon && !hasInsuranceStatusIcon) return false;
  if (hasAppointmentStatusIcon && hasInsuranceStatusIcon) {
    return getIsAppointmentTimeLessThanOrEqualTo30Minutes(duration);
  }
  return true;
};
